import React, { useMemo, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import { Grid, useMediaQuery } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import DialogContent from "@material-ui/core/DialogContent";
import { LogEvent } from "../../models/LogEvents";
import { LogListContainer } from "../LogListContainer";
import { Pagination } from "../Pagination";
import { MessageCard } from "../MessageCard";
import { DEFAULT_PAGING_SIZE, MIN_TABLE_HEIGHT, MAX_TABLE_HEIGHT } from "../../constants";
import { isCallSid, isChannelSid, getSidType, openLinkInNewTab } from "../../utils/index";
import { getServiceChannelDetailsUrl } from "../../utils/externalLinks";
import {
	getTwilioCallDetailsPage,
	getTwilioChatDetailsPage,
	getTwilioConferenceTrackingRecords,
} from "../../services/api/twilioDetails.api";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { FilterContainer } from "../FilterContainer";
import { GetConferenceTrackingRecordsResponse } from "../../models/TwilioModels";
import { ConferenceList } from "./TwilioConferenceList";
import { Button, Menu, MenuItem, Fade, CircularProgress } from "@material-ui/core";
import DvrIcon from "@material-ui/icons/Dvr";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import colors from "../../constants/colors";

export interface TwilioDetailsDialogProps {
	sid: string;
	detailsOpen: boolean;
	setOpen: (open: boolean) => void;
	canGoBack: boolean;
	subscriptionId: string;
	productId: string;
	accountSid: string;
	setConferenceData: (conference: GetConferenceTrackingRecordsResponse[]) => void;
	conferenceData: GetConferenceTrackingRecordsResponse[];
	navigateToNewConferenceSid: (newSid: string, oldSid: string) => void;
	navigateToLastConferenceSid: () => void;
	generateNewTabUrl: (sid: string) => string;
}

const useStyles = makeStyles((theme) => ({
	appBar: {
		position: "relative",
	},
	dialogPaper: {
		minHeight: "95vh",
		maxHeight: "100vh",
	},
	title: {
		marginLeft: theme.spacing(2),
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		flex: 1,
	},
	dialogBody: {
		margin: theme.spacing(4),
	},
	linkIcon: {
		paddingRight: "10px",
	},
}));

const renderList = (
	events: LogEvent[],
	_classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
) => {
	const eventsToRenderLength = events?.length || 0;
	return (
		<div>
			{eventsToRenderLength > 0 ? (
				<div>
					<div
						style={{
							minHeight: MIN_TABLE_HEIGHT,
							maxHeight: MAX_TABLE_HEIGHT,
							maxWidth: 900,
							overflow: "auto",
							borderBottomColor: colors.gray100,
							borderBottomWidth: 1,
							borderBottomStyle: "solid",
						}}
					>
						<LogListContainer logs={events}></LogListContainer>
					</div>
					<Pagination
						pageNumber={pageNumber}
						rowsPerPage={rowsPerPage}
						setPageFunction={setPageNumber}
						setRowsPerPageFunction={setRowsPerPage}
						currentPageLength={eventsToRenderLength}
					></Pagination>
				</div>
			) : (
				<MessageCard message="No Events found." />
			)}
		</div>
	);
};

/**
 * Twilio call and chat details dialog
 * @param props
 */
export const TwilioDetailsDialog = (props: TwilioDetailsDialogProps) => {
	const classes = useStyles();

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const [serviceSid, setServiceSid] = React.useState();
	const [logs, setLogs] = React.useState<LogEvent[]>([]);
	const [sid, setSid] = React.useState("");
	const isLoading = sid != props.sid;
	const theme = useTheme();

	const matches = useMediaQuery(theme.breakpoints.down("md"));

	let eventTypes: string[] = [];
	if (isCallSid(props.sid)) {
		eventTypes = [
			"taskRouter",
			"platformStatus",
			"runtimeDebugger",
			"browserFunction",
			"cloudFunction",
			"flowExecutionStep",
			"callEvent",
		];
	} else if (isChannelSid(props.sid)) {
		eventTypes = [
			"taskRouter",
			"platformStatus",
			"runtimeDebugger",
			"browserFunction",
			"cloudFunction",
			"flowExecutionStep",
		];
	}

	const [filterObject, setFilterObject] = React.useState({ eventTypeNames: eventTypes });

	const getUnifiedLogsRequest = {
		pageNumber: pageNumber,
		pageSize: rowsPerPage,
		requestedEventTypes: filterObject.eventTypeNames,
	};

	//Set Request API call.
	let dataRequestAPI;

	if (isCallSid(props.sid)) {
		dataRequestAPI = getTwilioCallDetailsPage(
			getUnifiedLogsRequest,
			props.productId,
			props.subscriptionId,
			props.accountSid,
			props.sid,
		);
	} else if (isChannelSid(props.sid)) {
		dataRequestAPI = getTwilioChatDetailsPage(
			getUnifiedLogsRequest,
			props.productId,
			props.subscriptionId,
			props.accountSid,
			props.sid,
		);
	}

	useEffect(() => {
		const getData = async () => {
			if (props.conferenceData.length == 0 && isCallSid(props.sid)) {
				const result = await getTwilioConferenceTrackingRecords(
					props.productId,
					props.subscriptionId,
					props.accountSid,
					props.sid.split(",")[0],
				)();
				props.setConferenceData(result.data);
			}
			const response = await dataRequestAPI();
			setServiceSid(response.data.serviceSid);
			setLogs(response.data.logs);
			setSid(props.sid);
		};
		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.productId, pageNumber, filterObject, props.sid]);

	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);
	const title = props.sid.replace(/,/g, ", ");
	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = (url) => {
		openLinkInNewTab(url);
		setAnchorEl(null);
	};

	return (
		<Dialog
			open={props.detailsOpen}
			onClose={() => {
				props.setOpen(false);
			}}
			classes={{ paper: classes.dialogPaper }}
			fullWidth={true}
			maxWidth={"xl"}
		>
			<AppBar color="default" className={classes.appBar}>
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={() => props.setOpen(false)} aria-label="close">
						<CloseIcon />
					</IconButton>
					{props.canGoBack && (
						<IconButton
							edge="start"
							color="inherit"
							onClick={() => props.navigateToLastConferenceSid()}
							aria-label="close"
						>
							<ArrowBackIcon />
						</IconButton>
					)}
					<Typography variant="h6" className={classes.title}>
						{`${getSidType(props.sid)}: ${title}`}
					</Typography>
					{isChannelSid(props.sid) && (
						<div style={{ float: "right" }}>
							<Button aria-controls="fade-menu" aria-haspopup="true" onClick={handleMenuOpen}>
								<MoreVertIcon />
							</Button>
							<Menu
								id="fade-menu"
								anchorEl={anchorEl}
								keepMounted
								open={open}
								onClose={handleMenuClose}
								TransitionComponent={Fade}
							>
								{isChannelSid(props.sid) && serviceSid && (
									<MenuItem onClick={() => handleMenuClose(getServiceChannelDetailsUrl(props.sid, serviceSid))}>
										<DvrIcon className={classes.linkIcon} />
										Service Channel Details
									</MenuItem>
								)}
							</Menu>
						</div>
					)}
				</Toolbar>
			</AppBar>
			<DialogContent>
				<div style={{ padding: 10 }}>
					<FilterContainer
						data-testid={"twilio-details-dialog-test-filter-container"}
						eventTypeFilter={{
							eventTypeNames: filterObject.eventTypeNames,
							selectedEventTypeNames: filterObject.eventTypeNames,
						}}
						onUpdate={setFilterObject}
					></FilterContainer>
				</div>
				<Grid container direction={matches ? "column-reverse" : "row"}>
					<Grid item lg={8} md={12} sm={12}>
						{useMemo(
							() =>
								isLoading ? (
									<CircularProgress />
								) : logs && logs.length == 0 ? (
									<MessageCard message="No events found." />
								) : (
									renderList(logs, classes, setPageNumber, setRowsPerPage, pageNumber, rowsPerPage)
								),
							[isLoading, logs, classes, pageNumber, rowsPerPage],
						)}
					</Grid>

					<Grid item lg={4} md={12} sm={12} style={{ padding: "8px 12px" }}>
						{isCallSid(props.sid) && (
							<ConferenceList
								navigateToNewConferenceSid={props.navigateToNewConferenceSid}
								conferenceData={props.conferenceData}
								generateNewTabUrl={props.generateNewTabUrl}
								sid={props.sid}
							></ConferenceList>
						)}
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};
